import {
  MAX_UMRISS_PUNKTE,
  MIN_UMRISS_PUNKTE,
  rundeUmriss,
  type Punkt,
  type Umriss,
} from "../../shared/umriss.ts";

/**
 * Aus einer Wischgeste wird ein Umriss (ARCHITEKTUR.md 6.2, Schritt 3).
 *
 * Alles hier ist **rein**: Zahlen rein, Zahlen raus, kein DOM, kein React. Das ist
 * Absicht — das Projekt hat kein jsdom und soll keins bekommen, und eine Geste lässt
 * sich ohne Browser nicht prüfen. Die Mathematik dagegen schon, und sie ist der Teil,
 * der still falsch sein kann: ein Umriss, der um zwei Prozent verrutscht, sieht richtig
 * aus und setzt die Person in Schritt 4 neben die Bodenlinie.
 *
 * Der Weg ist immer derselbe:
 *
 *   Zeigerpunkte in px → `ausduennen` → `vereinfachen` → Prozent → `rundeUmriss`
 *
 * Ausgedünnt und vereinfacht wird in **px**, nicht in Prozent. In Prozent sind die
 * beiden Achsen verschieden skaliert, sobald das Bild nicht quadratisch ist — ein
 * Abstand von „3" hieße waagerecht und senkrecht etwas anderes, und die Vereinfachung
 * würde hochkant anders schneiden als quer.
 */

/**
 * Zeigerpunkte unter diesem Abstand zum Vorgänger werden verworfen (ARCHITEKTUR.md 6.2).
 *
 * Eine Wischgeste liefert sonst über tausend Punkte — ein moderner Browser meldet
 * Zeigerereignisse mit der Bildrate des Displays, und auf 120 Hz sind das bei zwei
 * Sekunden Ziehen 240 Punkte allein aus der Zeit, plus alles, was `getCoalescedEvents`
 * nachreicht.
 */
export const MINDESTABSTAND_PX = 4;

/** Toleranz der Vereinfachung: 3 % der **Bildbreite** (ARCHITEKTUR.md 6.2). Ergibt
 *  rund 12 Punkte. Grob ist gewollt. */
export const TOLERANZ_ANTEIL = 0.03;

/**
 * Unterhalb dieses Anteils an der Bildfläche gilt die Geste als misslungen.
 *
 * ⚠️ **Ein Prozent ist gesetzt, nicht gemessen** — es steht in `TODO.md`. Belegt ist
 * nur, wogegen es schützt: ein kurzer Strich oder ein Tippen erzeugt sonst ein
 * Element ohne sichtbaren Inhalt, und die Kundschaft sieht ein leeres Kästchen statt
 * einer Person (ARCHITEKTUR.md 6.2, „Notausgang").
 */
export const MIN_FLAECHE_ANTEIL = 0.01;

/**
 * Der Rechteck-Rückfall: das ganze Bild.
 *
 * Er wird **ausgeschrieben gespeichert** und nicht als leeres Feld. ARCHITEKTUR.md 6.2
 * sagt „Leer = Rechteck-Rückfall"; das stimmt fürs Anzeigen, reicht aber nicht fürs
 * Bedienen: ein leeres Feld heißt sonst zugleich „noch nicht bearbeitet", und ein
 * Foto, das jemand bewusst auf Rechteck gestellt hat, käme in der Warteschlange wieder
 * hoch. Vier Punkte kosten nichts und trennen die beiden Fälle.
 */
export const RECHTECK: Umriss = [
  [0, 0],
  [100, 0],
  [100, 100],
  [0, 100],
];

/**
 * Was dasteht, wenn die Geste im Notausgang landet.
 *
 * Er steht hier und nicht in der Oberfläche, damit ihn ein Test lesen kann, ohne eine
 * Komponente zu rendern — dasselbe Muster wie `HINWEIS_ZU_KLEIN` in `vorschau.ts`.
 */
export const HINWEIS_NOTAUSGANG =
  "Das war zu wenig zum Erkennen — ich habe erst mal ein Rechteck genommen. " +
  "Fahr gern nochmal drumherum.";

/** Punkte, die zu dicht beieinander liegen, fallen weg. */
export function ausduennen(pfad: readonly Punkt[], mindestAbstand: number): Punkt[] {
  const behalten: Punkt[] = [];
  for (const punkt of pfad) {
    const letzter = behalten[behalten.length - 1];
    if (!letzter || Math.hypot(punkt[0] - letzter[0], punkt[1] - letzter[1]) >= mindestAbstand) {
      behalten.push(punkt);
    }
  }
  return behalten;
}

/**
 * Abstand eines Punktes zur **Strecke** a–b, nicht zur unendlichen Geraden.
 *
 * Der Unterschied zählt bei einem geschlossenen Umriss: dort liegen Anfang und Ende
 * nah beieinander, und die Gerade durch beide zieht quer durchs Bild. Gegen die Gerade
 * gemessen sähe jeder Punkt der Rückseite nah aus und fiele weg.
 */
function abstandZurStrecke(p: Punkt, a: Punkt, b: Punkt): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const laengeQuadrat = dx * dx + dy * dy;
  if (laengeQuadrat === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / laengeQuadrat;
  const geklemmt = Math.min(1, Math.max(0, t));
  return Math.hypot(p[0] - (a[0] + geklemmt * dx), p[1] - (a[1] + geklemmt * dy));
}

/** Ramer-Douglas-Peucker, ein Durchgang mit fester Toleranz. */
function rdp(pfad: readonly Punkt[], toleranz: number): Punkt[] {
  const erst = pfad[0];
  const letzt = pfad[pfad.length - 1];
  if (pfad.length <= 2 || !erst || !letzt) return [...pfad];

  let groessterAbstand = -1;
  let index = 0;
  for (const [i, punkt] of pfad.entries()) {
    if (i === 0 || i === pfad.length - 1) continue;
    const abstand = abstandZurStrecke(punkt, erst, letzt);
    if (abstand > groessterAbstand) {
      groessterAbstand = abstand;
      index = i;
    }
  }

  if (groessterAbstand <= toleranz) return [erst, letzt];
  const links = rdp(pfad.slice(0, index + 1), toleranz);
  const rechts = rdp(pfad.slice(index), toleranz);
  return [...links.slice(0, -1), ...rechts];
}

/** Reißleine für die Verdopplungsschleife. Bei einer Toleranz über der Bilddiagonale
 *  bleiben zwangsläufig zwei Punkte übrig; 32 Verdopplungen sind das Vielfache davon. */
const MAX_VERDOPPLUNGEN = 32;

/**
 * Vereinfachen bis höchstens `MAX_UMRISS_PUNKTE` übrig sind.
 *
 * Bleiben nach dem ersten Durchgang mehr Punkte übrig, wird die Toleranz **verdoppelt**
 * und erneut gelaufen, statt hinten abzuschneiden. Abschneiden würde den Umriss
 * aufreißen — die letzten Punkte sind der Weg zurück zum Anfang.
 */
export function vereinfachen(pfad: readonly Punkt[], toleranz: number): Punkt[] {
  let aktuell = toleranz > 0 ? toleranz : 1;
  let ergebnis = rdp(pfad, aktuell);
  for (let runde = 0; ergebnis.length > MAX_UMRISS_PUNKTE && runde < MAX_VERDOPPLUNGEN; runde++) {
    aktuell *= 2;
    ergebnis = rdp(pfad, aktuell);
  }
  return ergebnis;
}

/**
 * Fläche nach der Gauß'schen Trapezformel.
 *
 * ⚠️ Nicht das Hüllrechteck: ein dünner Zickzack über das halbe Bild hat ein großes
 * Rechteck und so gut wie keine Fläche — und genau so sieht eine misslungene Geste aus.
 */
export function flaeche(umriss: readonly Punkt[]): number {
  if (umriss.length < MIN_UMRISS_PUNKTE) return 0;
  let summe = 0;
  let [vx, vy] = umriss[umriss.length - 1] ?? [0, 0];
  for (const [x, y] of umriss) {
    summe += vx * y - x * vy;
    vx = x;
    vy = y;
  }
  return Math.abs(summe) / 2;
}

/**
 * Ist die Geste misslungen? Dann greift der Notausgang (ARCHITEKTUR.md 6.2).
 *
 * ⚠️ Hier stand bis zur Mutationsprobe am 24.09.2026 zusätzlich `if (umriss.length <
 * MIN_UMRISS_PUNKTE) return true;`. Die Zeile ließ sich **löschen, ohne dass ein Test
 * rot wurde** — und zwar nicht aus Testmangel: `flaeche` meldet unter drei Punkten
 * null, und null liegt unter jeder Schwelle. Die Prüfung war also unerreichbar. Eine
 * Zeile, die kein Eingabewert auslösen kann, ist keine Sicherung, sondern eine
 * Behauptung; sie ist raus. Die Mindestpunktzahl erzwingt `istGueltigerUmriss` am
 * Server, und dort wird sie von Tests gehalten.
 */
export function istUnbrauchbar(umriss: readonly Punkt[]): boolean {
  // In Prozent ist das ganze Bild 100 × 100 groß, seine Fläche also 10.000.
  return flaeche(umriss) < MIN_FLAECHE_ANTEIL * 100 * 100;
}

export interface Huellrechteck {
  links: number;
  oben: number;
  rechts: number;
  unten: number;
}

/**
 * Das umschließende Rechteck in Prozent.
 *
 * Daraus fallen die drei Größen ab, für die der Umriss überhaupt gebaut wird
 * (ARCHITEKTUR.md 6.2): die **Unterkante** ist die Bodenlinie, die **Höhe** die
 * scheinbare Größe, die **Breite** die Spaltenbreite in der Reihe von Schritt 4.
 */
export function huellrechteck(umriss: readonly Punkt[]): Huellrechteck {
  let links = 100;
  let oben = 100;
  let rechts = 0;
  let unten = 0;
  for (const [x, y] of umriss) {
    links = Math.min(links, x);
    oben = Math.min(oben, y);
    rechts = Math.max(rechts, x);
    unten = Math.max(unten, y);
  }
  return umriss.length === 0
    ? { links: 0, oben: 0, rechts: 100, unten: 100 }
    : { links, oben, rechts, unten };
}

/**
 * Der Umriss als `clip-path`.
 *
 * Prozent statt Pixel, direkt auf dem `<img>`: das passt ohne Umrechnung zum
 * Datenmodell und läuft auf der GPU statt über eine Zeichenfläche (ARCHITEKTUR.md 6.2).
 * Damit gilt es unverändert für die 110-px-Kachel, den Vollbildschirm und später den
 * Export.
 */
export function alsClipPath(umriss: readonly Punkt[]): string {
  const punkte = umriss.map(([x, y]) => `${String(x)}% ${String(y)}%`).join(", ");
  return `polygon(${punkte})`;
}

/**
 * Der ganze Weg von der Geste zum gespeicherten Umriss.
 *
 * Gibt `null` zurück, wenn die Geste im Notausgang landet — dann setzt der Aufrufer
 * `RECHTECK` und zeigt `HINWEIS_NOTAUSGANG`. Die Entscheidung liegt bewusst nicht
 * hier: ein deutscher Satz gehört an die Oberfläche, nicht in die Geometrie.
 *
 * `boxBreite` und `boxHoehe` sind die Maße der **angezeigten** Fläche in px. Auf welche
 * Fassung des Bildes gezeichnet wurde, ist danach gleichgültig — Prozent gelten für
 * Vorschau, Original und Export gleichermaßen (ARCHITEKTUR.md 6.5).
 */
export function ausGeste(
  pfad: readonly Punkt[],
  boxBreite: number,
  boxHoehe: number,
): Umriss | null {
  if (boxBreite <= 0 || boxHoehe <= 0) return null;

  const ausgeduennt = ausduennen(pfad, MINDESTABSTAND_PX);
  const vereinfacht = vereinfachen(ausgeduennt, TOLERANZ_ANTEIL * boxBreite);
  const inProzent: Umriss = vereinfacht.map(([x, y]) => [
    (x / boxBreite) * 100,
    (y / boxHoehe) * 100,
  ]);
  const gerundet = rundeUmriss(inProzent);

  return istUnbrauchbar(gerundet) ? null : gerundet;
}

/** `-0` ist rechnerisch null, schreibt sich aber als `-0%` in die Seite. */
function ohneMinusNull(wert: number): number {
  return wert === 0 ? 0 : wert;
}

/** Wie eine Kachel das Bild zeigt, wenn nur der Umriss zu sehen sein soll. */
export interface Ausschnitt {
  /** Breite zu Höhe der Kachel. Bei fester Höhe fällt daraus die Breite — und damit
   *  die Spaltenbreite aus ARCHITEKTUR.md 6.2. */
  seitenverhaeltnis: number;
  /** Wie groß das Bild **in Prozent der Kachel** gezeichnet wird. Über 100, weil ein
   *  Ausschnitt kleiner ist als das Ganze. */
  bildBreite: number;
  bildHoehe: number;
  /** Wohin das Bild geschoben wird, in Prozent der Kachel. Negativ — es hängt links
   *  und oben heraus, und die Kachel schneidet ab. */
  versatzLinks: number;
  versatzOben: number;
}

/**
 * Das Hüllrechteck als Ausschnitt — die Kachel zeigt **die Person**, nicht das Foto.
 *
 * ⚠️ Der Unterschied ist der, den Carolin am 24.09.2026 auf ihrem Telefon gesehen hat:
 * `clip-path` schneidet die Person aus, lässt die Element-Box aber so groß wie das
 * ganze Foto. Steht eine Person auf einem Drittel der Bildbreite, sind zwei Drittel der
 * Kachel leer — und zwischen vier Personen liegt mehr Weißraum als Person. Das
 * Hüllrechteck war von Anfang an dafür da (ARCHITEKTUR.md 6.2, „Breite ergibt die
 * Spaltenbreite"); es wurde nur nicht benutzt.
 *
 * Gerechnet wird in Prozent, damit dieselbe Rechnung für die 130-px-Kachel, den
 * Vollbildschirm und später den Export gilt. `bildBreite`/`bildHoehe` sind die Maße des
 * aufrecht dekodierten Bildes (`shared/umriss.ts`) und bestimmen allein das
 * Seitenverhältnis — sie skalieren nichts.
 */
export function ausschnitt(
  umriss: readonly Punkt[],
  bildBreite: number,
  bildHoehe: number,
): Ausschnitt {
  const ganz: Ausschnitt = {
    seitenverhaeltnis: bildBreite > 0 && bildHoehe > 0 ? bildBreite / bildHoehe : 1,
    bildBreite: 100,
    bildHoehe: 100,
    versatzLinks: 0,
    versatzOben: 0,
  };
  if (umriss.length === 0 || bildBreite <= 0 || bildHoehe <= 0) return ganz;

  const { links, oben, rechts, unten } = huellrechteck(umriss);
  const breite = rechts - links;
  const hoehe = unten - oben;
  // Ein Umriss ohne Ausdehnung würde durch null teilen. Er kommt hier nicht an — der
  // Notausgang fängt ihn vorher ab —, aber eine Division, die nur „eigentlich nicht"
  // passieren kann, ist keine.
  if (breite <= 0 || hoehe <= 0) return ganz;

  return {
    seitenverhaeltnis: (breite * bildBreite) / (hoehe * bildHoehe),
    bildBreite: (100 / breite) * 100,
    bildHoehe: (100 / hoehe) * 100,
    // `ohneMinusNull`, weil `-0 / x * 100` minus null ergibt und daraus in der Seite
    // ein `left: -0%` würde. Rechnerisch gleich, im Markup Unsinn.
    versatzLinks: ohneMinusNull((-links / breite) * 100),
    versatzOben: ohneMinusNull((-oben / hoehe) * 100),
  };
}
